import React from 'react';
import {Box, SxProps} from "@mui/material";
import {Theme, styled} from "@mui/system";

const Root = styled(Box, {
  shouldForwardProp: (prop) => (
    prop !== 'expanded' && prop !== 'variant'
  )
})(({theme, expanded, variant}: {theme: Theme, expanded?: CollapsableCardContainerAdditionalProps['expanded'], variant?: CollapsableCardContainerVariant}) => ({
  marginBottom: theme.spacing(2),
  boxShadow: '0px 1px 3px 0px #0000001F',
  borderRadius: theme.spacing(2),
  padding: variant === 'flush' ? 0 : theme.spacing(2),
  backgroundColor: '#F5F5F5',
  overflow: 'auto',
  wordWrap: 'break-word',
  [theme.breakpoints.up('sm')]: {
    overflow: 'hidden',
  },
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: expanded ? 'flex-start' : 'center',
}));

/**
 * 'default' keeps the standard card gutters. 'flush' removes the container padding so the header can sit
 * against the card edges; the body is then responsible for its own padding.
 */
type CollapsableCardContainerVariant = 'default' | 'flush';

// Renamed instead of copying misnomer over, using the same naming convention as MUI causes issues (in is a language key)
type CollapsableCardContainerAdditionalProps = React.ComponentPropsWithoutRef<'div'> & {
  expanded?: boolean
  variant?: CollapsableCardContainerVariant
  sx?: SxProps<Theme>
}

/**
 * MUI Box component styled in the SecurSpace way. Used as the main container when creating a Collapsable Card
 * @param children children passed through to the internal Grid component
 * @param expanded tells the container whether the card is collapsed or not
 * @param variant 'default' (padded) or 'flush' (no container padding)
 * @param rest The rest of the Grid props that will be passed through to the internal Grid component
 * @constructor
 */
const CollapsableCardContainer = ({ children, ...rest}: CollapsableCardContainerAdditionalProps) => {
  return <Root {...rest}>
    { children }
  </Root>
}

export { CollapsableCardContainer };
